/**
 * Content translation catalogs for locales that are not authored inline. Content stays authored as
 * `t(en, vi)` in packages/core/src/content/**; other locales live in per-locale catalogs keyed by the exact
 * English source text (gettext style), split into one JSON file per content module
 * (`i18n/content/<locale>/<group>.json`, see docs/I18N.md). `localize()` checks the inline value first, then the
 * catalog, along the locale's fallback chain. Catalogs are registered at runtime (client: lazily on a locale switch;
 * server / tests: `loadContentLocale`).
 */
import type { Locale } from './locales';

const CATALOGS = new Map<Locale, Map<string, string>>();
let version = 0;

/** Add (or override) entries for a locale. Empty strings are ignored (= not translated yet). */
export function registerContentCatalog(locale: Locale, entries: Record<string, string>): void {
  let m = CATALOGS.get(locale);
  if (!m) CATALOGS.set(locale, (m = new Map()));
  for (const [k, v] of Object.entries(entries)) if (typeof v === 'string' && v.trim()) m.set(k, v);
  version++;
}

export const catalogLookup = (locale: Locale, en: string): string | undefined => CATALOGS.get(locale)?.get(en);
export const hasContentCatalog = (locale: Locale) => (CATALOGS.get(locale)?.size ?? 0) > 0;
/** Bumps on every registration (UI re-render key). */
export const contentCatalogVersion = () => version;
/** Every key of a locale's catalog (tooling: unused-key report). */
export const catalogKeys = (locale: Locale): string[] => [...(CATALOGS.get(locale)?.keys() ?? [])];
